import { IconX } from '@tabler/icons-react';
import React from 'react';
import { createPortal } from 'react-dom';

import { formatMaterialQuantity } from '@utils/materialQuantity';

export type MaterialIncomingState = 'IN_PRODUCTION' | 'IN_TRANSIT';

export type MaterialIncomingDetailRow = {
    /** Mother order number (in production) or shipment PO id (in transit). */
    ref: string;
    kind: 'ORDER' | 'SHIPMENT';
    status: string;
    /** Expected readiness date (in production) or arrival date (in transit). */
    expectedDate: string | null;
    quantity: number;
};

interface MaterialIncomingModalProps {
    isOpen: boolean;
    onClose: () => void;
    sku: string;
    materialName: string;
    unit: string;
    state: MaterialIncomingState;
    rows: MaterialIncomingDetailRow[];
}

const STATE_LABEL: Record<MaterialIncomingState, string> = {
    IN_PRODUCTION: 'In Production',
    IN_TRANSIT: 'In Transit',
};

const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MaterialIncomingModal: React.FC<MaterialIncomingModalProps> = ({
    isOpen,
    onClose,
    sku,
    materialName,
    unit,
    state,
    rows,
}) => {
    if (!isOpen) return null;

    const sorted = [...rows].sort((a, b) => {
        if (!a.expectedDate) return 1;
        if (!b.expectedDate) return -1;
        return new Date(a.expectedDate).getTime() - new Date(b.expectedDate).getTime();
    });
    const totalQty = sorted.reduce((sum, r) => sum + r.quantity, 0);

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-100 p-6 pb-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">
                            {STATE_LABEL[state]} — {materialName}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">{sku}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                        <IconX className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {sorted.length === 0 ? (
                        <div className="py-8 text-center text-sm text-slate-400">
                            No {STATE_LABEL[state].toLowerCase()} quantity for this material
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    <th className="pb-3 pr-4">
                                        {state === 'IN_TRANSIT' ? 'Shipment' : 'Order'}
                                    </th>
                                    <th className="pb-3 pr-4">Status</th>
                                    <th className="pb-3 pr-4">
                                        {state === 'IN_TRANSIT' ? 'Expected Arrival' : 'Expected Ready'}
                                    </th>
                                    <th className="pb-3 text-right">Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((row, i) => (
                                    <tr key={`${row.ref}-${i}`} className="border-b border-slate-50">
                                        <td className="py-3 pr-4 font-medium text-slate-900">
                                            {row.ref}
                                        </td>
                                        <td className="py-3 pr-4 text-slate-600">{row.status}</td>
                                        <td
                                            className="py-3 pr-4 text-slate-700"
                                            style={{ fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {formatDate(row.expectedDate)}
                                        </td>
                                        <td
                                            className="py-3 text-right font-semibold text-slate-900"
                                            style={{ fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {formatMaterialQuantity(row.quantity, unit)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t border-slate-200">
                                    <td
                                        colSpan={3}
                                        className="py-3 pr-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500"
                                    >
                                        Total
                                    </td>
                                    <td
                                        className="py-3 text-right font-bold text-slate-900"
                                        style={{ fontVariantNumeric: 'tabular-nums' }}
                                    >
                                        {formatMaterialQuantity(totalQty, unit)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default MaterialIncomingModal;
